/**
 * Dashboard Component
 *
 * Full-screen home view with large navigation buttons for main app areas.
 * Designed to be the entry point without nested sidebars.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../analytics';
import { EGO_FEATURE_ENABLED } from '../ego';
import { useAdvancedMode } from '../hooks/useAdvancedMode';
import './Dashboard.css';

const SiteIcon = () => (
  <svg className="dashboard-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const EgoIcon = () => (
  <svg className="dashboard-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <line x1="12" y1="2" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="22" />
    <line x1="2" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="22" y2="12" />
  </svg>
);

const SiteMapIcon = () => (
  <svg className="dashboard-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

export function Dashboard(): JSX.Element {
  const { track } = useAnalytics();
  // The site map is behind the advanced=true soft flag while it rolls out
  const { isAdvancedMode, getSearchString } = useAdvancedMode();

  // Track page view on mount
  useEffect(() => {
    track('Pocket Admin Dashboard Viewed');
  }, [track]);

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <h1 className="dashboard__title">Welcome to Toptracer Go Admin</h1>
        <p className="dashboard__subtitle">Select an area to get started</p>
      </div>

      <div className="dashboard__grid">
        <Link to="/site" className="dashboard-card">
          <SiteIcon />
          <h2 className="dashboard-card__title">Sites</h2>
          <p className="dashboard-card__description">
            Manage site configurations, QR codes, and site-specific settings
          </p>
        </Link>

        {isAdvancedMode && (
          <Link to={`/site-map${getSearchString()}`} className="dashboard-card">
            <SiteMapIcon />
            <h2 className="dashboard-card__title">Site Map</h2>
            <p className="dashboard-card__description">
              See where sites are active and how healthy they look, on a world map
            </p>
          </Link>
        )}

        {/* Future areas can be added here */}
        {/* Example:
        <Link to="/analytics" className="dashboard-card">
          <AnalyticsIcon />
          <h2 className="dashboard-card__title">Analytics</h2>
          <p className="dashboard-card__description">
            View usage statistics and performance metrics
          </p>
        </Link>
        */}

        {EGO_FEATURE_ENABLED && (
          <Link to="/ego" className="dashboard-card">
            <EgoIcon />
            <h2 className="dashboard-card__title">eGO</h2>
            <p className="dashboard-card__description">
              Set up and configure Toptracer eGO devices
            </p>
          </Link>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
